// frontend/src/pages/admin/AdminDashboard.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import GoogleMapsCard from '../../components/admin/GoogleMapsCard';
import adminService from '../../services/adminService';
import { useAuth } from '../../context/AuthContext';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    totalShops: 0,
    totalDeliveries: 0,
    todayDeliveries: 0,
    recentCompanies: []
  });

  // Эрхүүдийг шалгах
  const canViewDashboard = hasPermission('system.dashboard.view');
  const canViewCompanies = hasPermission('system.companies.view');
  const canCreateCompany = hasPermission('system.companies.create');
  const canViewReports = hasPermission('system.reports.view');
  const canViewSettings = hasPermission('system.settings.view');

  useEffect(() => {
    if (canViewDashboard) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [canViewDashboard]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getDashboardStats();
      
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Fetch stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Нийт компани',
      value: stats.totalCompanies,
      icon: '🏢',
      color: 'primary',
      change: `${stats.activeCompanies} идэвхтэй`,
      iconBg: 'bg-blue-500',
    },
    {
      title: 'Идэвхтэй компани',
      value: stats.activeCompanies,
      icon: '✅',
      color: 'success',
      change: stats.totalCompanies > 0 ? `${Math.round((stats.activeCompanies / stats.totalCompanies) * 100)}% идэвхтэй` : '0%',
      iconBg: 'bg-green-500',
    },
    {
      title: 'Онлайн шопууд',
      value: stats.totalShops,
      icon: '🏪',
      color: 'info',
      change: 'Нийт бүртгэлтэй',
      iconBg: 'bg-cyan-500',
    },
    {
      title: 'Нийт хүргэлт',
      value: stats.totalDeliveries.toLocaleString(),
      icon: '📦',
      color: 'warning',
      change: `+${stats.todayDeliveries} өнөөдөр`,
      iconBg: 'bg-amber-500',
    },
  ];

  // Эрхийн дагуу quick actions шүүх
  const allQuickActions = [
    {
      title: 'Шинэ компани нэмэх',
      description: 'Шинэ хүргэлтийн компани бүртгэх',
      icon: '➕',
      color: 'blue',
      onClick: () => navigate('/admin/companies'),
      permission: 'system.companies.create',
    },
    {
      title: 'Тайлан харах',
      description: 'Санхүү, хүргэлтийн тайлан',
      icon: '📊',
      color: 'green',
      onClick: () => navigate('/admin/reports'),
      permission: 'system.reports.view',
    },
    {
      title: 'Тохиргоо',
      description: 'Системийн тохиргоо өөрчлөх',
      icon: '⚙️',
      color: 'purple',
      onClick: () => navigate('/admin/settings'),
      permission: 'system.settings.view',
    },
  ];

  // Эрхтэй үйлдлүүдийг шүүх
  const quickActions = allQuickActions.filter(action => hasPermission(action.permission));

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="mb-6 md:mb-8 animate-fade-in">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Системийн удирдлага
        </h1>
        <p className="text-sm md:text-base text-gray-600">
          Бүх компаниудын мэдээлэл, статистик харах
        </p>
      </div>

       {/* Dashboard эрхгүй бол хоосон */}
      {!canViewDashboard ? null : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {statCards.map((stat, index) => (
              <div
                key={index}
                className={`stat-card ${stat.color} animate-scale-in`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="stat-title text-xs md:text-sm">{stat.title}</p>
                    <h3 className="stat-value text-xl md:text-2xl">{stat.value}</h3>
                  </div>
                  <div className={`w-12 h-12 md:w-14 md:h-14 ${stat.iconBg} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}>
                    <span className="text-2xl md:text-3xl">{stat.icon}</span>
                  </div>
                </div>
                <p className="stat-change text-xs md:text-sm">{stat.change}</p>
              </div>
            ))}
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Recent Companies - 2 columns */}
            <div className="lg:col-span-2 space-y-4">
              {/* Recent Companies Card - Компани харах эрхтэй бол */}
              {canViewCompanies && (
                <div className="admin-card animate-fade-in">
                  <h3 className="admin-card-header flex items-center gap-2 text-base md:text-lg">
                    <span>🏢</span>
                    Сүүлд нэмэгдсэн компаниуд
                  </h3>
                  <div className="space-y-3">
                    {loading ? (
                      [1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse flex items-center gap-4 p-4 rounded-xl bg-gray-50">
                          <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                          </div>
                        </div>
                      ))
                    ) : stats.recentCompanies.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">Компани олдсонгүй</p>
                    ) : (
                      stats.recentCompanies.map((company, i) => (
                        <div
                          key={i}
                          onClick={() => navigate(`/admin/companies/${company.id}`)}
                          className="flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-gradient-to-r from-gray-50 to-white hover:from-blue-50 hover:to-white transition border border-gray-100 hover:border-blue-200 cursor-pointer"
                        >
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
                            <span className="text-lg md:text-xl text-white">🏢</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 text-sm md:text-base truncate">{company.name}</h4>
                            <p className="text-xs md:text-sm text-gray-500">{company.shops} онлайн шоптой</p>
                          </div>
                          <span className={`badge ${company.status === 'active' ? 'badge-success' : 'badge-warning'} text-xs flex-shrink-0`}>
                            {company.status === 'active' ? 'Идэвхтэй' : 'Хүлээгдэж буй'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Google Maps Card */}
              <GoogleMapsCard />
            </div>

            {/* Quick Actions - 1 column */}
            {quickActions.length > 0 && (
              <div className="admin-card animate-fade-in" style={{ animationDelay: '100ms' }}>
                <h3 className="admin-card-header flex items-center gap-2 text-base md:text-lg">
                  <span>⚡</span>
                  Түргэн үйлдлүүд
                </h3>
                <div className="space-y-3">
                  {quickActions.map((action, index) => (
                    <button
                      key={index}
                      onClick={action.onClick}
                      className={`w-full p-4 rounded-xl text-left transition-all hover:shadow-lg border-2 border-transparent hover:border-${action.color}-200 bg-gradient-to-r from-${action.color}-50 to-white hover:from-${action.color}-100`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-${action.color}-500 flex items-center justify-center flex-shrink-0 shadow-md`}>
                          <span className="text-xl text-white">{action.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm mb-1">{action.title}</h4>
                          <p className="text-xs text-gray-600">{action.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}

                  {/* System Health */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-xl border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-lg flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-green-900">Систем хэвийн ажиллаж байна</p>
                        <p className="text-xs text-green-700">Сүүлд шалгасан: Яг одоо</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* System Info */}
          <div className="admin-card mt-6 animate-fade-in" style={{ animationDelay: '200ms' }}>
            <h3 className="admin-card-header flex items-center gap-2 text-base md:text-lg">
              <span>ℹ️</span>
              Системийн мэдээлэл
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
                <p className="text-xs md:text-sm text-blue-700 mb-1 font-medium">Хувилбар</p>
                <p className="text-xl md:text-2xl font-bold text-blue-900">v1.0.0</p>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
                <p className="text-xs md:text-sm text-purple-700 mb-1 font-medium">Сүүлд шинэчилсэн</p>
                <p className="text-xl md:text-2xl font-bold text-purple-900">2024-12-20</p>
              </div>
              <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
                <p className="text-xs md:text-sm text-green-700 mb-1 font-medium">Серверийн статус</p>
                <p className="text-xl md:text-2xl font-bold text-green-900 flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse flex-shrink-0"></span>
                  Идэвхтэй
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;